/**
 * Zustand store for companion daemon status and active executions.
 *
 * Tracks whether the companion is connected to Django, and maintains
 * a map of currently running executions with their status.
 */

import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";
import { isTauri } from "@/lib/is-tauri";

import type { ConnectionStatus } from "@/api/websocket";

/**
 * Desktop-only grace period before the "companion disconnected" flag is
 * committed to the store. macOS sleep, transient network blips, and Django
 * deploy restarts all show up as a momentary disconnect that the WebSocket
 * layer self-recovers from within ~1-2s. Without this delay the wrench
 * would flash and any click on it would trigger an unnecessary subprocess
 * bounce. See Z-1301 for the failure mode this guards against.
 *
 * Reconnects always flip the status instantly (no grace) — only the
 * transition INTO `disconnected` is debounced.
 */
const DISCONNECTED_GRACE_MS = 5_000;

/** Number of restart failures within ESCALATION_WINDOW_MS that triggers the
 *  "please quit and reopen Denker" banner. Bounces older than the window
 *  fall out, so a single bad week-old reconnect doesn't escalate. */
const ESCALATION_THRESHOLD = 3;
const ESCALATION_WINDOW_MS = 5 * 60_000;

export interface AgentSession {
  executionId: string;
  spaceId: string;
  /** Denker runtime segment UUID (PK of executions.Session). */
  runtimeSessionId?: string;
  /** Durable chat identity. Required before a follow-up can be routed. */
  conversationId?: string;
  conversationFrameId?: string;
}

/** Live token usage for a session, streamed from the backend on execution.completed. */
export interface SessionTokens {
  /** Runtime segment that produced this snapshot. */
  runtimeSessionId?: string;
  /** Durable conversation that owns this snapshot. */
  conversationId?: string;
  /** Cumulative input tokens across the session (for cost display). */
  input: number;
  /** Cumulative output tokens across the session (for cost display). */
  output: number;
  /** Most recent turn's input tokens — the live context-window occupancy.
   *  Use this (not the cumulative ``input``) for the context ring, since
   *  every CLI turn re-sends the full conversation and summing inflates. */
  lastInput?: number;
  /** Latest provider-normalized context occupancy; null is explicitly unknown. */
  contextUsed?: number | null;
  /** Most recent turn's output tokens. */
  lastOutput?: number;
  /** Most recent turn's hidden reasoning tokens. */
  lastReasoning?: number;
  /** Context window cap in tokens — null means unknown (frontend may fall back to a model map). */
  max: number | null;
}

export interface AvailableRuntime {
  type: "claude_cli" | "codex_cli" | "opencode_cli";
  /** Stable key for this concrete runtime. Defaults to `type` for legacy companions. */
  runtime_id?: string;
  version: string;
  path: string;
  /** Best-effort CLI/provider authentication check; null when inconclusive. */
  logged_in?: boolean | null;
  /** Whether the provider accepted or rejected the current OAuth token. */
  auth_verified?: boolean | null;
  /** Terminal command the user runs themselves to (re-)log in. */
  login_command?: string;
  /** True for Denker-managed bundled runtimes that are not installed globally. */
  managed?: boolean;
  source?: string;
  latest_version?: string;
  update_available?: boolean;
  update_command?: string;
}

export interface AvailableModel {
  id: string;
  free: boolean;
  provider: string;
  /** Polished label reported by the companion ("Claude Opus 4.7"). */
  display_name?: string;
  /** Max tokens the model accepts. 0/undefined = companion didn't report. */
  context_window?: number;
  /** Whether the model takes image input; undefined = the CLI catalog doesn't say. */
  image_input?: boolean;
  /** Supported thinking-effort values. Empty = no reasoning knob. */
  reasoning_efforts?: string[];
  /** Other ids that resolve to this same model (e.g. ["opus"]). */
  aliases?: string[];
}

/** Model lists keyed by runtime identity, with adapter-type keys kept for legacy companions. */
export type AvailableModelsMap = Partial<Record<string, AvailableModel[]>>;

export type CompanionStatus = "disconnected" | "connected";

/** WebSocket-level connection state — re-exported from websocket module. */
export type WsConnectionState = ConnectionStatus;

export type ExecutionStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "cancelled"
  | "waiting_approval"
  | "paused";

export interface ExecutionTodo {
  id: string;
  title: string;
  status: "pending" | "running" | "completed" | "failed";
}

export interface ActiveExecution {
  id: string;
  adapterType?: string;
  status: ExecutionStatus;
  nativeSubagent?: boolean;
  runtimeSessionId?: string;
  conversationId?: string;
  agentName?: string;
  agentKey?: string;
  spaceId?: string;
  taskId?: string;
  startedAt?: string;
  /** Error message from the backend when status === "failed". */
  errorMessage?: string;
  todos: ExecutionTodo[];
  /** Wake reason for this execution — "chat" for user-driven, "heartbeat" for background polls. */
  origin?: "chat" | "heartbeat";
}

interface CompanionState {
  status: CompanionStatus;
  wsConnectionState: WsConnectionState;
  /** Callback to trigger an immediate reconnect (set by the owner connection). */
  triggerReconnect: (() => void) | null;
  /** Callback to resolve directory permission (retry or skip). */
  sendPermissionResolved: ((executionId: string, skip: boolean) => void) | null;
  /** Callback to ask the companion to re-detect installed CLI runtimes. */
  sendRuntimeRefresh: ((forceMetadata?: boolean) => void) | null;
  executions: Map<string, ActiveExecution>;
  currentExecutionId: string | null;
  /** True while the agent is actively responding (resets on execution.completed). */
  isAgentResponding: boolean;
  /** Companion's local event receiver port (for browser wake signal). */
  eventPort: number | null;
  /** Per-agent session tracking — keyed by "agentKey:spaceId". */
  agentSessions: Record<string, AgentSession>;
  /** Live session token usage — keyed by "agentKey:spaceId". */
  sessionTokens: Record<string, SessionTokens>;
  /** Whether the current user has a companion token provisioned.
   *  `null` = not yet fetched. Used to suppress "Agent Runner offline"
   *  UI before the desktop app's bundled companion has provisioned one. */
  hasToken: boolean | null;
  availableRuntimes: AvailableRuntime[];
  defaultRuntime: string;
  availableModels: AvailableModelsMap;
  /** Sliding window of recent restart-attempt failures (millis since epoch).
   *  Older than ESCALATION_WINDOW_MS are pruned on each write. When the
   *  window-bounded length crosses ESCALATION_THRESHOLD the
   *  `restartEscalated` flag flips on and the connection-health disconnect
   *  toast tells the user to quit and reopen Denker. Reset by
   *  `recordRestartRecovered`, which is called when
   *  a wrench-initiated restart actually reaches `connected`, AND
   *  automatically when `setStatus("connected")` fires. */
  recentRestartFailures: number[];
  restartEscalated: boolean;
  /** Why the native companion failed to start this session, shown by the
   *  disconnect toast once onboarding no longer owns the screen. Cleared on
   *  connect and on `clear()`. */
  startFailureMessage: string | null;

  setStatus: (status: CompanionStatus) => void;
  setEventPort: (port: number | null) => void;
  setWsConnectionState: (state: WsConnectionState) => void;
  setTriggerReconnect: (fn: (() => void) | null) => void;
  setSendPermissionResolved: (
    fn: ((executionId: string, skip: boolean) => void) | null,
  ) => void;
  setHasToken: (value: boolean | null) => void;
  setAvailableRuntimes: (
    runtimes: AvailableRuntime[],
    defaultRuntime: string,
  ) => void;
  setAvailableModels: (models: AvailableModelsMap) => void;
  setSendRuntimeRefresh: (
    fn: ((forceMetadata?: boolean) => void) | null,
  ) => void;

  /** Wrench-driven restart did not lead to `connected` within the verify
   *  window (or the IPC itself failed). Pushes a timestamp into the sliding
   *  window and flips `restartEscalated` when the threshold is crossed. */
  recordRestartFailure: () => void;
  /** A wrench-driven restart reached `connected`. Clears the failure window
   *  and escalation flag so subsequent isolated blips don't immediately
   *  re-escalate. */
  recordRestartRecovered: () => void;
  setStartFailureMessage: (message: string | null) => void;

  addExecution: (execution: ActiveExecution) => void;
  /** Replace the full active-execution map with the server-authoritative
   *  list across ALL of the user's projects and spaces. Clears stale
   *  entries the per-space WS channel missed. */
  syncAllExecutions: (executions: ActiveExecution[]) => void;
  updateExecution: (
    executionId: string,
    updates: Partial<ActiveExecution>,
  ) => void;
  removeExecution: (executionId: string) => void;
  setCurrentExecution: (id: string | null) => void;
  setExecutionTodos: (executionId: string, todos: ExecutionTodo[]) => void;
  turnCompleted: (executionId: string) => void;
  /** Re-assert that an agent is actively responding (e.g. cursor activity arrived). */
  restoreAgentResponding: (executionId: string) => void;

  setAgentSession: (
    agentKey: string,
    spaceId: string,
    executionId: string,
    runtimeSessionId?: string,
    conversationId?: string,
    conversationFrameId?: string,
  ) => void;
  clearAgentSession: (agentKey: string, spaceId: string) => void;
  clearSessionByExecution: (executionId: string) => void;
  getActiveExecution: (agentKey: string, spaceId: string) => string | null;
  getActiveSession: (agentKey: string, spaceId: string) => AgentSession | null;
  setSessionTokens: (
    agentKey: string,
    spaceId: string,
    tokens: SessionTokens,
  ) => void;

  clear: () => void;
}

// Module-scoped timer for the desktop disconnect grace period. The store is
// a process-wide singleton, so a single shared handle is correct — a member
// field on the zustand state would re-render on every reschedule.
let disconnectGraceTimer: ReturnType<typeof setTimeout> | null = null;

export const useCompanionStore = create<CompanionState>()(
  mirroredStore("companion", (set, get) => ({
    status: "disconnected",
    wsConnectionState: "disconnected",
    triggerReconnect: null,
    sendPermissionResolved: null,
    sendRuntimeRefresh: null,
    executions: new Map(),
    currentExecutionId: null,
    isAgentResponding: false,
    eventPort: null,
    agentSessions: {},
    sessionTokens: {},
    hasToken: null,
    availableRuntimes: [],
    defaultRuntime: "claude_cli",
    availableModels: {},
    recentRestartFailures: [],
    restartEscalated: false,
    startFailureMessage: null,

    setStatus: (status) => {
      // Web (non-Tauri) keeps the historic immediate flip — the wrench is
      // wired to the legacy "open settings" affordance there and we don't
      // want to change that surface in this fix.
      if (!isTauri()) {
        set(
          status === "connected"
            ? { status, startFailureMessage: null }
            : { status },
        );
        return;
      }
      // Connect flips are always immediate and cancel any pending grace timer.
      // Successful reconnects also clear the wrench-failure escalation window
      // so a single overnight glitch followed by recovery doesn't leave the
      // "please restart Denker" banner showing on a healthy app.
      if (status === "connected") {
        if (disconnectGraceTimer !== null) {
          clearTimeout(disconnectGraceTimer);
          disconnectGraceTimer = null;
        }
        set({
          status: "connected",
          recentRestartFailures: [],
          restartEscalated: false,
          startFailureMessage: null,
        });
        return;
      }
      // Disconnect: if we're already disconnected or a grace timer is running,
      // do nothing — the eventual flip is already accounted for.
      if (get().status === "disconnected" || disconnectGraceTimer !== null) {
        return;
      }
      disconnectGraceTimer = setTimeout(() => {
        disconnectGraceTimer = null;
        set({ status: "disconnected" });
      }, DISCONNECTED_GRACE_MS);
    },
    setEventPort: (port) => set({ eventPort: port }),
    setHasToken: (value) => set({ hasToken: value }),
    setAvailableRuntimes: (runtimes, defaultRuntime) =>
      set({ availableRuntimes: runtimes, defaultRuntime }),
    setAvailableModels: (models) => set({ availableModels: models }),
    setSendRuntimeRefresh: (fn) => set({ sendRuntimeRefresh: fn }),

    recordRestartFailure: () => {
      const now = Date.now();
      const cutoff = now - ESCALATION_WINDOW_MS;
      const next = [...get().recentRestartFailures, now].filter(
        (t) => t >= cutoff,
      );
      set({
        recentRestartFailures: next,
        restartEscalated: next.length >= ESCALATION_THRESHOLD,
      });
    },

    recordRestartRecovered: () => {
      set({ recentRestartFailures: [], restartEscalated: false });
    },

    setStartFailureMessage: (message) => set({ startFailureMessage: message }),

    setWsConnectionState: (state) => set({ wsConnectionState: state }),

    setTriggerReconnect: (fn) => set({ triggerReconnect: fn }),
    setSendPermissionResolved: (fn) => set({ sendPermissionResolved: fn }),

    addExecution: (execution) =>
      set((state) => {
        const next = new Map(state.executions);
        next.set(execution.id, {
          ...execution,
          todos: execution.todos ?? [],
        });
        const isActive =
          !execution.nativeSubagent &&
          (execution.status === "running" ||
            execution.status === "waiting_approval");
        return {
          executions: next,
          currentExecutionId: isActive
            ? execution.id
            : state.currentExecutionId,
          isAgentResponding: isActive ? true : state.isAgentResponding,
        };
      }),

    syncAllExecutions: (executions) =>
      set((state) => {
        // Rebuild the map from server truth. Preserve locally-tracked todos
        // on entries the server still knows about.
        const next = new Map<string, ActiveExecution>();
        for (const exec of executions) {
          const existing = state.executions.get(exec.id);
          next.set(exec.id, {
            ...exec,
            todos: existing?.todos ?? exec.todos ?? [],
          });
        }

        // Keep currentExecutionId if still valid; otherwise fall back to the
        // most recently started running execution so the stop button can
        // target it on page refresh.
        let currentId = state.currentExecutionId;
        if (
          !currentId ||
          !next.has(currentId) ||
          next.get(currentId)?.nativeSubagent
        ) {
          currentId = null;
          let latest: ActiveExecution | null = null;
          for (const exec of next.values()) {
            if (
              exec.nativeSubagent ||
              (exec.status !== "running" && exec.status !== "waiting_approval")
            ) {
              continue;
            }
            if (
              !latest ||
              (exec.startedAt &&
                latest.startedAt &&
                exec.startedAt > latest.startedAt)
            ) {
              latest = exec;
            }
          }
          if (latest) currentId = latest.id;
        }

        const anyRunning = Array.from(next.values()).some(
          (e) =>
            !e.nativeSubagent &&
            (e.status === "running" || e.status === "waiting_approval"),
        );

        return {
          executions: next,
          currentExecutionId: currentId,
          isAgentResponding: anyRunning,
        };
      }),

    updateExecution: (executionId, updates) =>
      set((state) => {
        const existing = state.executions.get(executionId);
        if (!existing) {
          // Execution wasn't tracked (e.g. follow-up reusing an old id).
          // Create a synthetic entry so completion clears properly.
          if (
            updates.status === "completed" ||
            updates.status === "failed" ||
            updates.status === "cancelled"
          ) {
            const next = new Map(state.executions);
            next.set(executionId, {
              id: executionId,
              status: updates.status,
              todos: [],
              ...updates,
              origin: updates.origin ?? "chat",
            } as ActiveExecution);
            return { executions: next, isAgentResponding: false };
          }
          return state;
        }
        const next = new Map(state.executions);
        // A finished run leaves no todo spinning: running ones take its outcome.
        const outcome =
          updates.status === "completed" || updates.status === "failed"
            ? updates.status
            : null;
        const todos = outcome
          ? existing.todos.map((t) =>
              t.status === "running" ? { ...t, status: outcome } : t,
            )
          : existing.todos;

        const updated = { ...existing, ...updates, todos };
        next.set(executionId, updated);
        if (updated.nativeSubagent) return { executions: next };
        const terminalStatuses: ExecutionStatus[] = [
          "completed",
          "failed",
          "cancelled",
        ];
        const isTerminal = updates.status
          ? terminalStatuses.includes(updates.status)
          : false;
        const isActive =
          updates.status === "running" || updates.status === "waiting_approval";
        // Restore currentExecutionId when an execution resumes running
        // (e.g. follow-up message after previous turn completed).
        const shouldRestore = isActive && !state.currentExecutionId;

        // Determine new currentExecutionId based on status transitions.
        // Keep currentExecutionId on "completed" so follow-up messages route
        // through the same execution (session continuity). Clear on failure/cancel.
        const newState: Partial<CompanionState> = { executions: next };
        if (updates.status === "failed" || updates.status === "cancelled") {
          if (state.currentExecutionId === executionId) {
            newState.currentExecutionId = null;
          }
        } else if (updates.status === "paused") {
          if (state.currentExecutionId === executionId) {
            const hasOtherRunning = Array.from(next.values()).some(
              (e) =>
                !e.nativeSubagent &&
                e.id !== executionId &&
                (e.status === "running" || e.status === "waiting_approval"),
            );
            if (!hasOtherRunning) {
              newState.currentExecutionId = null;
            }
          }
        } else if (shouldRestore) {
          newState.currentExecutionId = executionId;
        }

        // When a terminal status arrives, check if OTHER executions are still
        // running before clearing — with multi-agent one completing shouldn't
        // hide the stop button while another is still going.
        let responding: boolean;
        if (isTerminal) {
          responding = Array.from(next.values()).some(
            (e) =>
              !e.nativeSubagent &&
              e.id !== executionId &&
              (e.status === "running" || e.status === "waiting_approval"),
          );
        } else if (isActive) {
          responding = true;
        } else {
          responding = state.isAgentResponding;
        }

        return { ...newState, isAgentResponding: responding };
      }),

    removeExecution: (executionId) =>
      set((state) => {
        const next = new Map(state.executions);
        next.delete(executionId);
        return {
          executions: next,
          currentExecutionId:
            state.currentExecutionId === executionId
              ? null
              : state.currentExecutionId,
        };
      }),

    setCurrentExecution: (id) => set({ currentExecutionId: id }),

    setExecutionTodos: (executionId, todos) => {
      set((state) => {
        const exec = state.executions.get(executionId);
        if (!exec) return state;
        const updated = new Map(state.executions);
        updated.set(executionId, { ...exec, todos });
        return { executions: updated };
      });
    },

    turnCompleted: (executionId) => {
      set((state) => {
        // Only clear isAgentResponding if no OTHER execution is still running.
        // With multi-agent, one agent's turn completing shouldn't hide the
        // stop button while another agent is still streaming.
        const otherRunning = Array.from(state.executions.values()).some(
          (e) =>
            e.id !== executionId &&
            (e.status === "running" || e.status === "waiting_approval"),
        );
        return { isAgentResponding: otherRunning };
      });
    },

    restoreAgentResponding: (executionId) => {
      set((state) => {
        if (state.isAgentResponding) return state; // already true
        const exec = state.executions.get(executionId);
        if (
          exec &&
          (exec.status === "running" || exec.status === "waiting_approval")
        ) {
          return { isAgentResponding: true };
        }
        return state;
      });
    },

    setAgentSession: (
      agentKey,
      spaceId,
      executionId,
      runtimeSessionId,
      conversationId,
      conversationFrameId,
    ) =>
      set((s) => {
        const key = `${agentKey}:${spaceId}`;
        const prev = s.agentSessions[key];
        // Preserve a previously-resolved runtime segment when the new event
        // doesn't carry one (the very first execution.status="running"
        // arrives before the backend has linked the Session row).
        const nextRuntimeSessionId = runtimeSessionId ?? prev?.runtimeSessionId;
        const nextConversationId = conversationId ?? prev?.conversationId;
        const sessionChanged = Boolean(
          runtimeSessionId &&
          prev?.runtimeSessionId &&
          runtimeSessionId !== prev.runtimeSessionId,
        );
        const sessionTokens = sessionChanged
          ? Object.fromEntries(
              Object.entries(s.sessionTokens).filter(
                ([tokenKey]) => tokenKey !== key,
              ),
            )
          : s.sessionTokens;
        return {
          agentSessions: {
            ...s.agentSessions,
            [key]: {
              executionId,
              spaceId,
              ...(nextRuntimeSessionId
                ? { runtimeSessionId: nextRuntimeSessionId }
                : {}),
              ...(nextConversationId
                ? { conversationId: nextConversationId }
                : {}),
              ...((conversationFrameId ?? prev?.conversationFrameId)
                ? {
                    conversationFrameId:
                      conversationFrameId ?? prev?.conversationFrameId,
                  }
                : {}),
            },
          },
          sessionTokens,
        };
      }),

    clearAgentSession: (agentKey, spaceId) =>
      set((s) => {
        const key = `${agentKey}:${spaceId}`;
        const { [key]: _, ...rest } = s.agentSessions;
        return { agentSessions: rest };
      }),

    clearSessionByExecution: (executionId) =>
      set((s) => {
        const filtered: Record<string, AgentSession> = {};
        for (const [key, session] of Object.entries(s.agentSessions)) {
          if (session.executionId !== executionId) {
            filtered[key] = session;
          }
        }
        return { agentSessions: filtered };
      }),

    getActiveExecution: (agentKey, spaceId) => {
      const session = get().agentSessions[`${agentKey}:${spaceId}`];
      return session &&
        !get().executions.get(session.executionId)?.nativeSubagent
        ? session.executionId
        : null;
    },

    getActiveSession: (agentKey, spaceId) => {
      const session = get().agentSessions[`${agentKey}:${spaceId}`];
      return session &&
        !get().executions.get(session.executionId)?.nativeSubagent
        ? session
        : null;
    },

    setSessionTokens: (agentKey, spaceId, tokens) =>
      set((s) => ({
        sessionTokens: {
          ...s.sessionTokens,
          [`${agentKey}:${spaceId}`]: tokens,
        },
      })),

    clear: () =>
      set({
        status: "disconnected",
        wsConnectionState: "disconnected",
        triggerReconnect: null,
        sendRuntimeRefresh: null,
        executions: new Map(),
        currentExecutionId: null,
        isAgentResponding: false,
        eventPort: null,
        agentSessions: {},
        sessionTokens: {},
        availableRuntimes: [],
        defaultRuntime: "claude_cli",
        availableModels: {},
        hasToken: null,
        recentRestartFailures: [],
        restartEscalated: false,
        startFailureMessage: null,
      }),
  })),
);
